import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Folder, Plus, X, CircleNotch, UsersThree, ListChecks, ArrowRight } from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * ProjectsList — grid of the user's projects. New Project modal + tile
 * click opens the workspace at /app/projects/{id}.
 */
export default function ProjectsList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [urlParams, setUrlParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(urlParams.get("new") === "1");
  const navigate = useNavigate();

  // Clear the ?new=1 hint after we've consumed it so back nav doesn't reopen.
  useEffect(() => {
    if (urlParams.get("new") === "1") {
      const next = new URLSearchParams(urlParams);
      next.delete("new");
      setUrlParams(next, { replace: true });
    }
  }, [urlParams, setUrlParams]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(Array.isArray(data) ? data : []);
    } catch { setProjects([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const createProject = async (payload) => {
    const { data } = await api.post("/projects", payload);
    setCreateOpen(false);
    navigate(`/app/projects/${data.project_id}`);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="projects-list">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#F2DC8C]/60">
            Command Center · Projects
          </div>
          <h1 className="mt-1 font-display text-[34px] leading-tight"
              style={{
                background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                letterSpacing: "0.02em",
              }}>
            Your Workspaces
          </h1>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          data-testid="projects-new"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-xl font-display uppercase text-[11px] tracking-[0.22em] transition-transform active:scale-95"
          style={{
            background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)",
            color: "#0a0703",
            boxShadow: "0 6px 20px rgba(255,215,0,0.35)",
          }}>
          <Plus size={13} weight="bold"/> New Project
        </button>
      </header>

      {loading ? (
        <div className="rounded-2xl p-10 text-center" data-testid="projects-loading"
             style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(0,0,0,0.4)" }}>
          <CircleNotch size={22} className="animate-spin text-[#FFD700] mx-auto"/>
        </div>
      ) : projects.length === 0 ? (
        <div className="rounded-2xl p-10 text-center"
             data-testid="projects-empty"
             style={{
               background: "linear-gradient(180deg, rgba(26,18,6,0.75) 0%, rgba(5,3,0,0.9) 100%)",
               border: "1px solid rgba(212,175,55,0.45)",
             }}>
          <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center"
               style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
            <Folder size={22} weight="fill" color="#0a0703"/>
          </div>
          <div className="mt-4 font-display text-[24px] text-[#FFD700]">No workspaces yet.</div>
          <div className="mt-1 text-[13px] text-[#F2DC8C]/70">
            Spin up a project to organise tasks, files, and teammates in one place.
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="projects-empty-create"
            className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-xl font-display uppercase text-[11px] tracking-[0.22em]"
            style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
            <Plus size={12} weight="bold"/> New Project
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
          {projects.map((p) => (
            <ProjectTile key={p.project_id} project={p}/>
          ))}
        </div>
      )}

      <AnimatePresence>
        {createOpen && <NewProjectModal onClose={() => setCreateOpen(false)} onCreate={createProject}/>}
      </AnimatePresence>
    </div>
  );
}


function ProjectTile({ project }) {
  const total = project.tasks_total || 0;
  const done = project.tasks_done || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <Link
      to={`/app/projects/${project.project_id}`}
      data-testid={`project-tile-${project.project_id}`}
      className="group block rounded-2xl p-5 transition-all hover:-translate-y-0.5"
      style={{
        background:
          "radial-gradient(circle at 100% 0%, rgba(255,215,0,0.08) 0%, transparent 55%)," +
          "linear-gradient(180deg, rgba(26,18,6,0.75) 0%, rgba(5,3,0,0.9) 100%)",
        border: "1px solid rgba(212,175,55,0.45)",
        boxShadow: "0 6px 18px rgba(0,0,0,0.45)",
      }}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="font-display text-[20px] text-[#FFD700] leading-tight truncate">
            {project.name}
          </div>
          {project.description && (
            <div className="mt-1 text-[12px] text-[#F2DC8C]/70 line-clamp-2">
              {project.description}
            </div>
          )}
        </div>
        <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
          <Folder size={16} weight="fill" color="#0a0703"/>
        </span>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#F2DC8C]/60 mb-1">
          <span>Progress</span>
          <span className="tabular-nums text-[#FFD700]">{pct}%</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden"
             style={{ background: "rgba(212,175,55,0.15)" }}>
          <div className="h-full transition-all"
               style={{
                 width: `${pct}%`,
                 background: "linear-gradient(90deg, #FFE58A, #FFD700 60%, #D4AF37)",
                 boxShadow: "0 0 8px rgba(255,215,0,0.4)",
               }}/>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-widest text-[#F2DC8C]/60 group-hover:text-[#FFD700] transition-colors">
        <span className="inline-flex items-center gap-1"><ListChecks size={11}/> {done}/{total} tasks</span>
        <ArrowRight size={12}/>
      </div>
    </Link>
  );
}


export function NewProjectModal({ onClose, onCreate, defaultTeamId }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [teamId, setTeamId] = useState(defaultTeamId || "");
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // If we're already scoped to a team (creating from team detail),
    // don't bother loading the picker.
    if (defaultTeamId) return;
    (async () => {
      try { const { data } = await api.get("/teams"); setTeams(data || []); }
      catch { setTeams([]); }
    })();
  }, [defaultTeamId]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await onCreate({
        name: name.trim(),
        description: description.trim() || null,
        team_id: teamId || null,
      });
    } finally { setBusy(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(14px)" }}
      onClick={onClose}
      data-testid="project-modal">
      <motion.form
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        transition={{ type: "spring", stiffness: 320, damping: 30 }}
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl overflow-hidden"
        style={{
          background: "linear-gradient(180deg, rgba(20,14,4,0.98) 0%, rgba(5,3,0,1) 100%)",
          border: "1.5px solid rgba(255,215,0,0.55)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.75), 0 0 40px rgba(255,215,0,0.18)",
        }}>
        <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: "rgba(212,175,55,0.35)" }}>
          <Folder size={16} weight="fill" className="text-[#FFD700]"/>
          <div className="font-display text-[16px] text-[#FFD700]">New Project</div>
          <button type="button" onClick={onClose} data-testid="project-modal-close"
                  className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ border: "1px solid rgba(212,175,55,0.5)" }}>
            <X size={13} className="text-[#FFD700]"/>
          </button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block rounded-xl p-3"
                 style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(0,0,0,0.35)" }}>
            <div className="text-[9px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 mb-1">Name</div>
            <input autoFocus data-testid="project-name" value={name}
                   onChange={(e) => setName(e.target.value.slice(0, 140))}
                   placeholder="Aloha Studio Launch"
                   className="w-full bg-transparent text-[15px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none"/>
          </label>
          <label className="block rounded-xl p-3"
                 style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(0,0,0,0.35)" }}>
            <div className="text-[9px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 mb-1">Description (optional)</div>
            <textarea data-testid="project-desc" value={description}
                      onChange={(e) => setDescription(e.target.value.slice(0, 4000))} rows={3}
                      placeholder="What are we building?"
                      className="w-full bg-transparent text-[13px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none resize-none"/>
          </label>
          {!defaultTeamId && teams.length > 0 && (
            <label className="block rounded-xl p-3"
                   style={{ border: "1px solid rgba(212,175,55,0.35)", background: "rgba(0,0,0,0.35)" }}>
              <div className="text-[9px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 mb-1">Team (optional)</div>
              <select data-testid="project-team"
                      value={teamId}
                      onChange={(e) => setTeamId(e.target.value)}
                      className="w-full bg-transparent text-[13px] text-[#F2DC8C] focus:outline-none"
                      style={{ colorScheme: "dark" }}>
                <option value="" style={{ background: "#0a0703" }}>Just me</option>
                {teams.map((t) => (
                  <option key={t.team_id} value={t.team_id} style={{ background: "#0a0703" }}>
                    {t.name} ({t.member_count} member{t.member_count === 1 ? "" : "s"})
                  </option>
                ))}
              </select>
              <div className="text-[10px] text-[#F2DC8C]/50 mt-1">
                Attaching to a team shares this project with every member instantly.
              </div>
            </label>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t"
             style={{ borderColor: "rgba(212,175,55,0.35)" }}>
          <button type="button" onClick={onClose}
                  className="px-4 h-10 rounded-xl text-[11px] uppercase tracking-widest text-[#F2DC8C]"
                  style={{ border: "1px solid rgba(212,175,55,0.45)" }}>
            Cancel
          </button>
          <button type="submit"
                  disabled={busy || !name.trim()}
                  data-testid="project-save"
                  className="px-4 h-10 rounded-xl font-display uppercase text-[11px] tracking-[0.22em] disabled:opacity-40"
                  style={{
                    background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)",
                    color: "#0a0703",
                    boxShadow: "0 6px 20px rgba(255,215,0,0.35)",
                  }}>
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}
